import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Briefcase,
    Plus,
    Edit,
    Trash2,
    CheckCircle,
    X,
    Calendar,
    MapPin,
    FileText,
    Settings,
    Shield,
    Info,
    Check,
    Eye
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
    fetchOrdreMissionRequest,
    createOrdreMissionRequest,
    updateOrdreMissionRequest,
    deleteOrdreMissionRequest,
} from '../../../../redux/actions/ordreMissionActions';
import { fetchNoteDeFraisRequest } from '../../../../redux/actions/noteDeFraisActions';
import '../parametresStyles/ParamConges.css';

/* ─── Toggle component ───────────────────────────────────────── */
const Toggle = ({ checked, onChange, label, disabled }) => (
    <button
        type="button"
        className={`pcToggle ${checked ? "is-on" : ""}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
    >
        <span className="pcToggle__thumb" />
        {label && <span className="pcToggle__label">{label}</span>}
    </button>
);

/* ─── Tooltip component ──────────────────────────────────────── */
const Tip = ({ text }) => (
    <span className="pcTip" data-tip={text}>
        <Info size={13} />
    </span>
);

const ParamMissions = () => {
    const dispatch = useDispatch();
    const { ordresMission, loading, error } = useSelector((state) => state.ordreMission || {
        ordresMission: [],
        loading: false,
        error: null,
    });
    const { notesDeFrais } = useSelector((state) => state.noteDeFrais || { notesDeFrais: [] });

    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    
    const [formData, setFormData] = useState({});
    const [stepErrors, setStepErrors] = useState({});

    const workflowOptions = [
        { val: 'manager', icon: "👤", title: 'Manager', desc: "Validation manager uniquement." },
        { val: 'admin',   icon: "🏢", title: 'Admin RH', desc: "Validation RH uniquement." },
        { val: 'both',    icon: "🔐", title: 'Double', desc: "Manager puis RH." },
    ];

    useEffect(() => {
        dispatch(fetchOrdreMissionRequest());
        dispatch(fetchNoteDeFraisRequest());
    }, [dispatch]);

    /* form helpers */
    const set = useCallback((key, val) =>
        setFormData(p => ({ ...p, [key]: val })), []);

    const toggleNoteAuth = (noteId) => {
        setFormData(p => {
            const current = p.notesDeFraisAutoriseesIds || [];
            if (current.includes(noteId)) {
                return { ...p, notesDeFraisAutoriseesIds: current.filter(id => id !== noteId) };
            } else {
                return { ...p, notesDeFraisAutoriseesIds: [...current, noteId] };
            }
        });
    };

    const openModal = (mission = null) => {
        setStepErrors({});
        if (mission) {
            setEditItem(mission);
            const authorizedIds = mission.notesDeFraisAutorisees 
                ? mission.notesDeFraisAutorisees.map(nf => nf.id) 
                : [];

            setFormData({
                code: mission.code || '',
                libelle: mission.libelle || '',
                description: mission.description || '',
                dateDebut: mission.dateDebut || '',
                dateFin: mission.dateFin || '',
                lieu: mission.lieu || '',
                centreCout: mission.centreCout || '',
                workflow: mission.workflow || 'manager',
                active: mission.active ?? true,
                notesDeFraisAutoriseesIds: authorizedIds,
            });
        } else {
            setEditItem(null);
            setFormData({
                code: '',
                libelle: '',
                description: '',
                dateDebut: '',
                dateFin: '',
                lieu: '',
                centreCout: '',
                workflow: 'manager',
                active: true,
                notesDeFraisAutoriseesIds: [],
            });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditItem(null);
        setFormData({});
        setStepErrors({});
    };

    const openViewModal = (mission) => {
        setViewItem(mission);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setViewItem(null);
    };

    const validateForm = () => {
        const errs = {};
        if (!formData.code?.trim()) errs.code = "Requis";
        if (!formData.libelle?.trim()) errs.libelle = "Requis";
        if (!formData.dateDebut) errs.dateDebut = "Requis";
        if (!formData.dateFin) errs.dateFin = "Requis";
        
        if (formData.dateDebut && formData.dateFin) {
            if (new Date(formData.dateFin) < new Date(formData.dateDebut)) {
                errs.dateFin = "La fin doit être après le début";
            }
        }
        
        setStepErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const payload = {
            ...formData,
        };

        if (editItem) {
            dispatch(updateOrdreMissionRequest(editItem.id, payload));
        } else {
            dispatch(createOrdreMissionRequest(payload));
        }
        closeModal();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Voulez-vous vraiment supprimer cet ordre de mission ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            cancelButtonColor: '#204385',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteOrdreMissionRequest(id));
            }
        });
    };

    const toggleActive = (mission) => {
        dispatch(
            updateOrdreMissionRequest(mission.id, {
                ...mission,
                active: !mission.active,
            })
        );
    };

    const getWorkflowLabel = (workflow) => {
        const option = workflowOptions.find(opt => opt.val === workflow);
        return option ? option.title : workflow;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR');
    };

    const renderViewModal = () => {
        if (!showViewModal || !viewItem) return null;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true" style={{ maxWidth: '500px' }}>
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            Détails de la mission
                        </div>
                        <button className="pcIconBtn" onClick={closeViewModal} type="button" aria-label="Fermer">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div style={{ marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                                {viewItem.libelle}
                            </h3>
                            <span className="pcCode" style={{ backgroundColor: '#204385' }}>
                                {viewItem.code}
                            </span>
                        </div>

                        <div className="pcWizStep">
                            <div className="pcWizStep__intro" style={{ marginBottom: '15px' }}>
                                <div className="pcWizStep__introIcon"><FileText size={20} /></div>
                                <div>
                                    <div className="pcWizStep__introTitle">Notes de Frais Autorisées</div>
                                    <div className="pcWizStep__introSub">Les dépenses permises pour cette mission.</div>
                                </div>
                            </div>

                            {viewItem.notesDeFraisAutorisees && viewItem.notesDeFraisAutorisees.length > 0 ? (
                                <ul style={{ listStyleType: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {viewItem.notesDeFraisAutorisees.map(nf => (
                                        <li key={nf.id} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '10px 12px',
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '6px'
                                        }}>
                                            <span className="pcPill is-primary">{nf.code}</span>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                    {nf.libelle || nf.category}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                    {nf.devise || 'MAD'} {nf.montantMaximum ? `(Max: ${nf.montantMaximum})` : ''}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="pcEmptyBox" style={{ padding: '20px', textAlign: 'center' }}>
                                    Aucune note de frais autorisée.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--primary" type="button" onClick={closeViewModal}>
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderModal = () => {
        if (!showModal) return null;
        const isEdit = !!editItem;

        return (
            <div className="pcModal">
                <div className="pcModal__panel" role="dialog" aria-modal="true" style={{ maxWidth: '650px' }}>
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            {isEdit ? 'Modifier un ordre de mission' : 'Ajouter un ordre de mission'}
                        </div>
                        <button className="pcIconBtn" onClick={closeModal} type="button" aria-label="Fermer" disabled={loading}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pcModal__body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                        
                        {/* Section Identité */}
                        <div className="pcWizStep">
                            <div className="pcWizStep__intro">
                                <div className="pcWizStep__introIcon"><Briefcase size={20} /></div>
                                <div>
                                    <div className="pcWizStep__introTitle">Détails de la mission</div>
                                    <div className="pcWizStep__introSub">Code, libellé et description de l'événement.</div>
                                </div>
                            </div>

                            <div className="pcWizGrid">
                                <div className="pcWizField">
                                    <label>Code <span className="pcReq">*</span> <Tip text="Identifiant unique (ex: MISS-2026)" /></label>
                                    <input
                                        type="text"
                                        className={`pcInput ${stepErrors.code ? "is-error" : ""}`}
                                        value={formData.code || ""}
                                        onChange={e => set("code", e.target.value.toUpperCase())}
                                        placeholder="MISS-001"
                                    />
                                    {stepErrors.code && <span className="pcErrMsg">{stepErrors.code}</span>}
                                </div>

                                <div className="pcWizField is-full">
                                    <label>Libellé public <span className="pcReq">*</span></label>
                                    <input
                                        type="text"
                                        className={`pcInput ${stepErrors.libelle ? "is-error" : ""}`}
                                        value={formData.libelle || ""}
                                        onChange={e => set("libelle", e.target.value)}
                                        placeholder="Ex: Mission commerciale Paris"
                                    />
                                    {stepErrors.libelle && <span className="pcErrMsg">{stepErrors.libelle}</span>}
                                </div>

                                <div className="pcWizField is-full">
                                    <label>Description</label>
                                    <textarea
                                        className="pcInput pcInput--textarea"
                                        rows={2}
                                        value={formData.description || ""}
                                        onChange={e => set("description", e.target.value)}
                                        placeholder="Description de la mission..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section Lieu et Dates */}
                        <div className="pcWizStep" style={{ marginTop: '20px' }}>
                            <div className="pcWizStep__intro">
                                <div className="pcWizStep__introIcon"><MapPin size={20} /></div>
                                <div>
                                    <div className="pcWizStep__introTitle">Lieu & Dates</div>
                                    <div className="pcWizStep__introSub">Période et lieu d'exécution.</div>
                                </div>
                            </div>

                            <div className="pcWizGrid">
                                <div className="pcWizField">
                                    <label>Date de début <span className="pcReq">*</span></label>
                                    <input
                                        type="date"
                                        className={`pcInput ${stepErrors.dateDebut ? "is-error" : ""}`}
                                        value={formData.dateDebut || ""}
                                        onChange={e => set("dateDebut", e.target.value)}
                                    />
                                    {stepErrors.dateDebut && <span className="pcErrMsg">{stepErrors.dateDebut}</span>}
                                </div>

                                <div className="pcWizField">
                                    <label>Date de fin <span className="pcReq">*</span></label>
                                    <input
                                        type="date"
                                        className={`pcInput ${stepErrors.dateFin ? "is-error" : ""}`}
                                        value={formData.dateFin || ""}
                                        onChange={e => set("dateFin", e.target.value)}
                                    />
                                    {stepErrors.dateFin && <span className="pcErrMsg">{stepErrors.dateFin}</span>}
                                </div>

                                <div className="pcWizField">
                                    <label>Lieu de mission</label>
                                    <input
                                        type="text"
                                        className="pcInput"
                                        value={formData.lieu || ""}
                                        onChange={e => set("lieu", e.target.value)}
                                        placeholder="Ex: Paris, France"
                                    />
                                </div>

                                <div className="pcWizField">
                                    <label>Centre de Coûts</label>
                                    <input
                                        type="text"
                                        className="pcInput"
                                        value={formData.centreCout || ""}
                                        onChange={e => set("centreCout", e.target.value)}
                                        placeholder="Optionnel"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section Workflow & Notes de frais autorisées */}
                        <div className="pcWizStep" style={{ marginTop: '20px' }}>
                            <div className="pcWizStep__intro">
                                <div className="pcWizStep__introIcon"><Shield size={20} /></div>
                                <div>
                                    <div className="pcWizStep__introTitle">Validation & Frais Autorisés</div>
                                    <div className="pcWizStep__introSub">Définissez qui valide et quels types de frais le collaborateur peut déclarer pour cette mission.</div>
                                </div>
                            </div>

                            <div className="pcWizField is-full">
                                <label>Niveaux de validation des participations</label>
                                <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={formData.workflow === 'manager' || formData.workflow === 'both'}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                const current = formData.workflow || 'manager';
                                                const isAdmin = current === 'admin' || current === 'both';
                                                
                                                if (isChecked && isAdmin) set("workflow", "both");
                                                else if (isChecked) set("workflow", "manager");
                                                else if (isAdmin) set("workflow", "admin");
                                                else set("workflow", "manager"); // force at least one
                                            }}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Validation Manager</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={formData.workflow === 'admin' || formData.workflow === 'both'}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                const current = formData.workflow || 'manager';
                                                const isManager = current === 'manager' || current === 'both';
                                                
                                                if (isChecked && isManager) set("workflow", "both");
                                                else if (isChecked) set("workflow", "admin");
                                                else if (isManager) set("workflow", "manager");
                                                else set("workflow", "admin"); // force at least one
                                            }}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Validation Admin RH</span>
                                    </label>
                                </div>
                                <div style={{ fontSize: 11, color: "#64748b", marginTop: '8px' }}>
                                    Sélectionnez les niveaux requis pour l'approbation. Au moins un niveau est obligatoire.
                                </div>
                            </div>

                            <div className="pcWizField is-full" style={{ marginTop: '20px' }}>
                                <label>Notes de frais autorisées</label>
                                <div className="pcNotesList" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '10px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    backgroundColor: '#f8fafc'
                                }}>
                                    {(notesDeFrais || []).map(note => {
                                        const isChecked = (formData.notesDeFraisAutoriseesIds || []).includes(note.id);
                                        return (
                                            <label key={note.id} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                cursor: 'pointer',
                                                backgroundColor: isChecked ? '#eff6ff' : '#fff',
                                                padding: '8px 12px',
                                                borderRadius: '6px',
                                                border: `1px solid ${isChecked ? '#3b82f6' : '#cbd5e1'}`,
                                                transition: 'all 0.2s'
                                            }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={isChecked}
                                                    onChange={() => toggleNoteAuth(note.id)}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                />
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                                        {note.libelle || note.category}
                                                    </span>
                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>{note.code}</span>
                                                </div>
                                            </label>
                                        );
                                    })}
                                    {(notesDeFrais || []).length === 0 && (
                                        <div className="pcMuted" style={{ padding: '10px' }}>Aucune note de frais disponible.</div>
                                    )}
                                </div>
                            </div>

                            <div className="pcWizField is-full" style={{ marginTop: '20px' }}>
                                <div className="pcInfoBox">
                                    <Toggle checked={!!formData.active} onChange={v => set("active", v)} disabled={loading} />
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 12 }}>Mission Active</div>
                                        <div style={{ fontSize: 11, color: "#64748b" }}>Permet aux collaborateurs de s'y associer.</div>
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closeModal} disabled={loading}>
                            Annuler
                        </button>
                        <button className="pcBtn pcBtn--primary" type="button" onClick={handleSubmit} disabled={loading}>
                            {isEdit ? 'Enregistrer les modifications' : 'Créer la mission'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Gestion des ordres de mission</h1>
                    <p>Configurez les missions et associez-les aux notes de frais autorisées.</p>
                    {error && <div style={{ marginTop: 8, color: '#b91c1c', fontWeight: 700 }}>Erreur: {error}</div>}
                </div>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <h2 className="pcTitle">Missions paramétrables</h2>
                            <button
                                className="pcBtn pcBtn--primary"
                                type="button"
                                onClick={() => openModal()}
                                disabled={loading}
                            >
                                <Plus size={16} /> Ajouter une mission
                            </button>
                        </div>

                        <div className="pcTableWrap">
                            <table className="pcTable">
                                <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Libellé & Lieu</th>
                                    <th>Période</th>
                                    <th>Validation</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="pcEmptyCell">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : (ordresMission || []).length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="pcEmptyCell">
                                            Aucun ordre de mission configuré
                                        </td>
                                    </tr>
                                ) : (
                                    (ordresMission || []).map((mission) => (
                                        <tr key={mission.id}>
                                            <td>
                                                <span className="pcCode" style={{ backgroundColor: '#204385' }}>
                                                    {mission.code}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="pcStrong">{mission.libelle}</div>
                                                <div className="pcMuted" style={{ fontSize: '11px' }}>
                                                    {mission.lieu ? <><MapPin size={10} style={{ display: 'inline', marginRight: 2 }}/>{mission.lieu}</> : '-'}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="pcMuted" style={{ fontSize: '12px' }}>
                                                    Du: {formatDate(mission.dateDebut)}
                                                </div>
                                                <div className="pcMuted" style={{ fontSize: '12px' }}>
                                                    Au: {formatDate(mission.dateFin)}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="pcBadge">
                                                    {getWorkflowLabel(mission.workflow)}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className={`pcPill ${mission.active ? 'is-on' : 'is-off'}`}
                                                    type="button"
                                                    onClick={() => toggleActive(mission)}
                                                    disabled={loading}
                                                >
                                                    {mission.active ? 'Actif' : 'Inactif'}
                                                </button>
                                            </td>
                                            <td className="is-right">
                                                <div className="pcRowActions">
                                                    <button
                                                        className="pcIconBtn"
                                                        type="button"
                                                        title="Voir"
                                                        onClick={() => openViewModal(mission)}
                                                        disabled={loading}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button
                                                        className="pcIconBtn"
                                                        type="button"
                                                        title="Modifier"
                                                        onClick={() => openModal(mission)}
                                                        disabled={loading}
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button
                                                        className="pcIconBtn pcIconBtn--danger"
                                                        type="button"
                                                        title="Supprimer"
                                                        onClick={() => handleDelete(mission.id)}
                                                        disabled={loading}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {renderModal()}
            {renderViewModal()}
        </div>
    );
};

export default ParamMissions;
